import React, { useState } from 'react'
import { BlockInstance, BLOCKS_LIBRARY } from './types'
import { generateBlockHTML } from './blockGenerators'
import { Modal, Button } from 'react-bootstrap'
import { HTMLEditor } from '../HTMLEditor'
import { BlockConfigurationModal } from './BlockConfigurationModal'

interface EmailBlockBuilderProps {
    blocks: BlockInstance[]
    onChange: (blocks: BlockInstance[]) => void
    onAddBlock: (blockId: string) => void
    templateVariables: string[]
    canManageLockedBlocks?: boolean
}

export const EmailBlockBuilder: React.FC<EmailBlockBuilderProps> = ({
    blocks,
    onChange,
    onAddBlock,
    templateVariables,
    canManageLockedBlocks = false,
}) => {
    const [editingBlock, setEditingBlock] = useState<{ index: number, field: string, value: string } | null>(null)
    const [configBlock, setConfigBlock] = useState<{ index: number, instance: BlockInstance } | null>(null)
    const [showDeleteModal, setShowDeleteModal] = useState(false)
    const [blockToDelete, setBlockToDelete] = useState<number | null>(null)

    const createBlockInstanceId = () => `block-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`

    const moveBlock = (index: number, direction: 'up' | 'down') => {
        const newBlocks = [...blocks]
        const targetIndex = direction === 'up' ? index - 1 : index + 1
        if (targetIndex < 0 || targetIndex >= blocks.length) return
        const temp = newBlocks[index]
        newBlocks[index] = newBlocks[targetIndex]
        newBlocks[targetIndex] = temp
        onChange(newBlocks)
    }

    const duplicateBlock = (index: number) => {
        const sourceBlock = blocks[index]
        if (!sourceBlock) return

        const duplicatedBlock: BlockInstance = {
            ...sourceBlock,
            id: createBlockInstanceId(),
            data: JSON.parse(JSON.stringify(sourceBlock.data || {})),
        }

        const newBlocks = [...blocks]
        newBlocks.splice(index + 1, 0, duplicatedBlock)
        onChange(newBlocks)
    }

    const toggleBlockLock = (index: number) => {
        if (!canManageLockedBlocks) return

        const newBlocks = [...blocks]
        const currentBlock = newBlocks[index]
        if (!currentBlock) return

        newBlocks[index] = {
            ...currentBlock,
            locked: !currentBlock.locked,
            lockReason: !currentBlock.locked ? 'Protected blueprint content' : undefined,
        }

        onChange(newBlocks)
    }

    const deleteBlock = (index: number) => {
        setBlockToDelete(index)
        setShowDeleteModal(true)
    }

    const confirmDelete = () => {
        if (blockToDelete !== null) {
            onChange(blocks.filter((_, i) => i !== blockToDelete))
            setShowDeleteModal(false)
            setBlockToDelete(null)
        }
    }

    const updateBlockData = (index: number, field: string, value: any) => {
        const newBlocks = [...blocks]
        if (field === 'all') {
            // Merge existing content with new design settings
            newBlocks[index].data = { ...newBlocks[index].data, ...value }
        } else {
            newBlocks[index].data = { ...newBlocks[index].data, [field]: value }
        }
        onChange(newBlocks)
        setEditingBlock(null)
        setConfigBlock(null)
    }

    const renderBlockPreview = (block: BlockInstance, index: number) => {
        const html = generateBlockHTML(block.blockId, block.data)
        const hasContent = block.data.content || block.data.col1Content || block.data.col2Content || block.data.col3Content
        const isLocked = Boolean(block.locked)

        return (
            <div key={block.id} className="builder-block-wrapper mb-3 border rounded position-relative bg-white" style={{ minHeight: '120px' }}>
                {/* Block Label */}
                {(() => {
                    const blockDef = BLOCKS_LIBRARY.find(b => b.id === block.blockId)
                    return blockDef ? (
                        <div className="block-label position-absolute top-0 start-0 px-2 py-1" style={{ zIndex: 10 }}>
                            <div className="d-flex gap-2 align-items-center">
                                <span className="badge bg-light text-muted border fw-normal small">
                                    <i className={`bi ${blockDef.icon} me-1`}></i>{blockDef.label}
                                </span>
                                {isLocked && (
                                    <span className="badge bg-warning text-dark border fw-semibold small">
                                        <i className="bi bi-lock-fill me-1"></i>Locked
                                    </span>
                                )}
                            </div>
                        </div>
                    ) : null
                })()}
                {/* Overlay Controls */}
                <div className="block-controls position-absolute top-0 end-0 p-2 d-flex gap-1" style={{ zIndex: 10 }}>
                    <Button variant="light" size="sm" className="shadow-sm" onClick={() => moveBlock(index, 'up')} disabled={index === 0 || isLocked}>
                        <i className="bi bi-arrow-up"></i>
                    </Button>
                    <Button variant="light" size="sm" className="shadow-sm" onClick={() => moveBlock(index, 'down')} disabled={index === blocks.length - 1 || isLocked}>
                        <i className="bi bi-arrow-down"></i>
                    </Button>
                    <Button
                        variant="light"
                        size="sm"
                        className="shadow-sm text-primary"
                        onClick={() => setConfigBlock({ index, instance: block })}
                        disabled={isLocked}
                    >
                        <i className="bi bi-gear-fill"></i>
                    </Button>
                    <Button
                        variant="light"
                        size="sm"
                        className="shadow-sm text-info"
                        onClick={() => duplicateBlock(index)}
                        title="Duplicar bloque"
                        disabled={isLocked}
                    >
                        <i className="bi bi-files"></i>
                    </Button>
                    {canManageLockedBlocks && (
                        <Button
                            variant={isLocked ? "warning" : "light"}
                            size="sm"
                            className={`shadow-sm ${isLocked ? 'text-dark' : 'text-warning'}`}
                            onClick={() => toggleBlockLock(index)}
                            title={isLocked ? 'Desbloquear bloque' : 'Bloquear bloque'}
                        >
                            <i className={`bi ${isLocked ? 'bi-unlock-fill' : 'bi-lock-fill'}`}></i>
                        </Button>
                    )}
                    <Button variant="danger" size="sm" className="shadow-sm" onClick={() => deleteBlock(index)} disabled={isLocked}>
                        <i className="bi bi-trash"></i>
                    </Button>
                </div>

                {/* Pre-rendered HTML Content */}
                <div className="block-content-preview p-3" style={{ minHeight: '100px' }}>
                    {!hasContent && (
                        <div className="text-center py-4 text-muted fst-italic">
                            <i className="bi bi-pencil me-2"></i>
                            Haz clic en "Editar Texto" para agregar contenido
                        </div>
                    )}
                    {isLocked && (
                        <div className="alert alert-warning py-2 px-3 mb-3 small d-inline-flex align-items-center" role="alert">
                            <i className="bi bi-lock-fill me-2"></i>
                            {block.lockReason || 'This block is locked to preserve the core message.'}
                        </div>
                    )}
                    <div dangerouslySetInnerHTML={{ __html: html }} />
                </div>

                {/* Edit Buttons Overlay */}
                <div className="block-edit-overlay d-flex justify-content-center align-items-center">
                    <div className="edit-actions-group d-flex flex-wrap gap-2 justify-content-center p-3">
                        {block.blockId === '1_col' || block.blockId === 'hero' || block.blockId === 'button' ? (
                            <Button
                                variant="primary"
                                size="sm"
                                onClick={() => setEditingBlock({ index, field: block.blockId === 'button' ? 'content' : 'content', value: block.data.content || '' })}
                                disabled={isLocked}
                            >
                                <i className="bi bi-pencil me-1"></i> {block.blockId === 'button' ? 'Editar Texto' : 'Editar Texto'}
                            </Button>
                        ) : block.blockId === '2_cols' ? (
                            <>
                                <Button variant="primary" size="sm" onClick={() => setEditingBlock({ index, field: 'col1Content', value: block.data.col1Content || '' })} disabled={isLocked}>
                                    <i className="bi bi-pencil me-1"></i> Col 1
                                </Button>
                                <Button variant="primary" size="sm" onClick={() => setEditingBlock({ index, field: 'col2Content', value: block.data.col2Content || '' })} disabled={isLocked}>
                                    <i className="bi bi-pencil me-1"></i> Col 2
                                </Button>
                            </>
                        ) : block.blockId === '3_cols' ? (
                            <>
                                <Button variant="primary" size="sm" onClick={() => setEditingBlock({ index, field: 'col1Content', value: block.data.col1Content || '' })} disabled={isLocked}>
                                    <i className="bi bi-pencil me-1"></i> C1
                                </Button>
                                <Button variant="primary" size="sm" onClick={() => setEditingBlock({ index, field: 'col2Content', value: block.data.col2Content || '' })} disabled={isLocked}>
                                    <i className="bi bi-pencil me-1"></i> C2
                                </Button>
                                <Button variant="primary" size="sm" onClick={() => setEditingBlock({ index, field: 'col3Content', value: block.data.col3Content || '' })} disabled={isLocked}>
                                    <i className="bi bi-pencil me-1"></i> C3
                                </Button>
                            </>
                        ) : block.blockId === 'image_text' ? (
                            <Button variant="primary" size="sm" onClick={() => setEditingBlock({ index, field: 'content', value: block.data.content || '' })} disabled={isLocked}>
                                <i className="bi bi-pencil me-1"></i> Editar Texto
                            </Button>
                        ) : block.blockId === 'button' || block.blockId === 'icon_note' ? (
                            <Button variant="primary" size="sm" onClick={() => setConfigBlock({ index, instance: block })} disabled={isLocked}>
                                <i className="bi bi-pencil me-1"></i> {block.blockId === 'icon_note' ? 'Editar Nota' : 'Editar Botón'}
                            </Button>
                        ) : null}
                    </div>
                </div>

                <style>{`
                    .builder-block-wrapper {
                        transition: all 0.2s ease-in-out;
                        border: 2px solid transparent !important;
                        position: relative;
                        margin-bottom: 2px !important;
                    }
                    .builder-block-wrapper:hover {
                        border-color: #3699FF !important;
                        background: rgba(54, 153, 255, 0.02);
                    }
                    .block-label {
                        opacity: 0;
                        transition: all 0.2s;
                        transform: translateY(-4px);
                        pointer-events: none;
                    }
                    .builder-block-wrapper:hover .block-label {
                        opacity: 1;
                        transform: translateY(0);
                    }
                    .block-controls {
                        opacity: 0;
                        transition: all 0.2s;
                        background: white;
                        border: 1px solid #E4E6EF;
                        box-shadow: 0 0.5rem 1.5rem 0.5rem rgba(0, 0, 0, 0.075);
                        border-radius: 0.475rem;
                        transform: translateY(-10px);
                    }
                    .builder-block-wrapper:hover .block-controls {
                        opacity: 1;
                        transform: translateY(0);
                    }
                    .block-edit-overlay {
                        position: absolute;
                        top: 0;
                        left: 0;
                        right: 0;
                        bottom: 0;
                        background: rgba(54, 153, 255, 0.04);
                        opacity: 0;
                        transition: opacity 0.2s;
                        display: flex;
                        justify-content: center;
                        align-items: center;
                        z-index: 5;
                    }
                    .builder-block-wrapper:hover .block-edit-overlay {
                        opacity: 1;
                    }
                    .edit-actions-group {
                        transform: scale(0.9);
                        transition: transform 0.2s;
                        background: white;
                        padding: 8px 12px;
                        border-radius: 8px;
                        box-shadow: 0 2px 8px rgba(0,0,0,0.1);
                    }
                    .builder-block-wrapper:hover .edit-actions-group {
                        transform: scale(1);
                    }
                    .block-content-preview {
                        min-height: 80px;
                        position: relative;
                    }
                    /* Ensure tables inside preview don't overflow */
                    .block-content-preview table {
                        max-width: 100% !important;
                    }
                    .block-content-preview p {
                        margin-bottom: 0.5rem;
                    }
                `}</style>
            </div>
        )
    }

    const renderEmptyState = () => (
        <div className="text-center py-20 px-5 border-2 border-dashed rounded-3 bg-gray-50 opacity-75">
            <div className="mb-4">
                <i className="bi bi-layout-three-columns fs-3x text-gray-400"></i>
            </div>
            <h4 className="text-gray-700 fw-bold mb-2">Your canvas is empty</h4>
            <p className="text-gray-500 mb-8">Select a block from the left or use a quick action:</p>

            <div className="d-flex justify-content-center gap-3">
                <Button variant="outline-primary" size="sm" onClick={() => onAddBlock('1_col')}>
                    <i className="bi bi-square me-1"></i> + 1 Column
                </Button>
                <Button variant="outline-primary" size="sm" onClick={() => onAddBlock('hero')}>
                    <i className="bi bi-star-fill me-1"></i> + Hero
                </Button>
                <Button variant="outline-primary" size="sm" onClick={() => onAddBlock('2_cols')}>
                    <i className="bi bi-columns-gap me-1"></i> + 2 Columns
                </Button>
            </div>
        </div>
    )

    return (
        <div className="email-block-builder min-h-300px p-4 bg-gray-100" style={{ overflowY: 'visible' }}>
            {blocks.length === 0 ? (
                renderEmptyState()
            ) : (
                <div className="builder-canvas-inner p-3 bg-white shadow-sm rounded">
                    {blocks.map((block, index) => renderBlockPreview(block, index))}
                </div>
            )}

            {/* Content Editor Modal */}
            <Modal show={editingBlock !== null} onHide={() => setEditingBlock(null)} size="lg" centered className="builder-content-modal">
                <Modal.Header closeButton>
                    <Modal.Title>Edit Content</Modal.Title>
                </Modal.Header>
                <Modal.Body className="p-0">
                    {editingBlock && (
                        <HTMLEditor
                            value={editingBlock.value}
                            onChange={(val) => setEditingBlock({ ...editingBlock, value: val })}
                            height={400}
                            variables={templateVariables}
                            mode='embedded'
                        />
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setEditingBlock(null)}>Close</Button>
                    <Button variant="primary" onClick={() => editingBlock && updateBlockData(editingBlock.index, editingBlock.field, editingBlock.value)}>
                        Save Changes
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Settings / Configuration Modal */}
            {configBlock && (
                <BlockConfigurationModal
                    show={configBlock !== null}
                    onHide={() => setConfigBlock(null)}
                    block={BLOCKS_LIBRARY.find(b => b.id === configBlock.instance.blockId) || null}
                    initialData={configBlock.instance.data}
                    onConfirm={(newData) => updateBlockData(configBlock.index, 'all', newData)}
                />
            )}

            {/* Delete Block Confirmation Modal */}
            <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} centered>
                <Modal.Header closeButton className='border-0 pb-0'>
                    <Modal.Title>Delete Block</Modal.Title>
                </Modal.Header>
                <Modal.Body className='pt-3 pb-4'>
                    <div className='alert alert-warning d-flex align-items-start' role='alert'>
                        <i className='bi bi-exclamation-circle me-2 fs-4'></i>
                        <div>
                            Are you sure you want to delete this block?
                            {blockToDelete !== null && blocks[blockToDelete] && (
                                <div className='mt-2 small'>
                                    Block type: <strong>{BLOCKS_LIBRARY.find(b => b.id === blocks[blockToDelete].blockId)?.label || 'Unknown'}</strong>
                                </div>
                            )}
                            <div className='mt-2 small'>
                                This action cannot be undone and any content in this block will be lost.
                            </div>
                        </div>
                    </div>
                </Modal.Body>
                <Modal.Footer className='border-0 justify-content-end'>
                    <Button variant='light' onClick={() => setShowDeleteModal(false)}>
                        Cancel
                    </Button>
                    <Button variant='danger' onClick={confirmDelete}>
                        Delete
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    )
}
